import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */
/* @jsx mdx */
import DefaultLayout from "/home/runner/work/commerce-pwa-studio/commerce-pwa-studio/node_modules/@adobe/gatsby-theme-aio/src/components/MDXFilter/index.js";
export const _frontmatter = {};
const layoutProps = {
  _frontmatter
};
const MDXLayout = DefaultLayout;
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">


    <h1 {...{
      "id": "modify-talon-results"
    }}>{`Modify talon results`}</h1>
    <p>{`The `}<a parentName="p" {...{
        "href": "/commerce/pwa-studio/api/peregrine/targets/"
      }}>{`talons target`}</a>{` in Peregrine exposes talons as wrappable modules.
This lets you define a wrapper module that intercepts a talon and let you modify the results.`}</p>
    <h2 {...{
      "id": "background"
    }}>{`Background`}</h2>
    <p>{`In core Adobe Commerce and Magento Open Source development:`}</p>
    <blockquote>
      <p parentName="blockquote">{`A plugin, or interceptor, is a class that modifies the behavior of public class functions by intercepting a function call and running code before, after, or around that function call.
This allows you to substitute or extend the behavior of original, public methods for any class or interface.`}</p>
    </blockquote>
    <p>{`See: `}<a parentName="p" {...{
        "href": "https://developer.adobe.com/commerce/php/development/components/plugins/"
      }}>{`Plugins (Interceptors)`}</a></p>
    <p>{`PWA Studio's extensibility framework provides a similar feature that allows you to intercept a talon call and surround it with custom logic.
This is useful if you want to add tracking logic or alter the incoming or outgoing values for a talon.`}</p>
    <p>{`This tutorial teaches you how to create an extension that intercepts a talon and changes the results.`}</p>
    <h2 {...{
      "id": "tasks-overview"
    }}>{`Tasks overview`}</h2>
    <ol>
      <li parentName="ol">{`Initialize the project`}</li>
      <li parentName="ol">{`Create and register an intercept file`}</li>
      <li parentName="ol">{`Define the intercept file`}</li>
      <li parentName="ol">{`Make a data fetch hook`}</li>
      <li parentName="ol">{`Create the talon wrapper module`}</li>
      <li parentName="ol">{`Update the package entry point`}</li>
      <li parentName="ol">{`Test on a local instance`}</li>
    </ol>
    <h2 {...{
      "id": "initialize-the-project"
    }}>{`Initialize the project`}</h2>
    <p>{`To intercept and wrap a talon, you need a PWA Studio extension.
Use `}<a parentName="p" {...{
        "href": "https://docs.npmjs.com/cli/init"
      }}><inlineCode parentName="a">{`npm init`}</inlineCode></a>{` or `}<a parentName="p" {...{
        "href": "https://classic.yarnpkg.com/en/docs/cli/init/"
      }}><inlineCode parentName="a">{`yarn init`}</inlineCode></a>{` to create a new JavaScript package project for this tutorial.
Since, this is a standalone project, you do not need to create this inside a storefront project.`}</p>
    <p>{`Edit the `}<inlineCode parentName="p">{`packages.json`}</inlineCode>{` file so it looks like the following:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-json"
      }}>{`{
  "name": "my-extension",
  "version": "0.0.1",
  "description": "A PWA Studio extension",
  "publishConfig": {
    "access": "public"
  },
  "main": "index.js",
  "license": "(OSL-3.0 OR AFL-3.0)",
  "repository": "",
  "dependencies": {},
  "peerDependencies": {
    "@magento/peregrine": "~7.0.0",
    "@magento/pwa-buildpack": "~6.0.0",
    "@magento/venia-ui": "~4.0.0",
    "apollo-client": "2.6.4",
    "graphql-tag": "~2.10.1",
    "react": "~17.0.1",
    "webpack": "~4.38.0"
  }
}
`}</code></pre>
    <h2 {...{
      "id": "create-and-register-the-intercept-file"
    }}>{`Create and register the intercept file`}</h2>
    <p>{`You can create the intercept file anywhere in your project.
For this tutorial, create this file under `}<inlineCode parentName="p">{`src/targets`}</inlineCode>{`.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-sh"
      }}>{`mkdir -p src/targets && touch src/targets/my-intercept.js
`}</code></pre>
    <p>{`Set the value for `}<inlineCode parentName="p">{`pwa-studio.targets.intercept`}</inlineCode>{` in your project's `}<inlineCode parentName="p">{`package.json`}</inlineCode>{` file to tell the build process where to find the intercept file.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-diff"
      }}>{`    "react": "~17.0.1",
    "webpack": "~4.38.0"
  },
+ "pwa-studio": {
+   "targets": {
+     "intercept": "src/targets/my-intercept"
+   }
+ }
}
`}</code></pre>
    <h2 {...{
      "id": "define-the-intercept-file"
    }}>{`Define the intercept file`}</h2>
    <p>{`The intercept file is where you tap into PWA Studio's extensibility framework and add your modifications.`}</p>
    <p>{`In your intercept file, add the following content:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-js"
      }}>{`module.exports = (targets) => {
  // Wrap the useProductFullDetail talon with this extension
  const peregrineTargets = targets.of("@magento/peregrine");
  const talonsTarget = peregrineTargets.talons;

  talonsTarget.tap((talonWrapperConfig) => {
    talonWrapperConfig.ProductFullDetail.useProductFullDetail.wrapWith(
      "my-extension"
    );
  });

  // Set the buildpack features required by this extension
  const builtins = targets.of("@magento/pwa-buildpack");
  builtins.specialFeatures.tap((featuresByModule) => {
    featuresByModule["@my-extension/my-product-page"] = {
      // Wrapper modules must be ES Modules
      esModules: true,
    };
  });
};
`}</code></pre>
    <p>{`When this file runs, it taps into the `}<inlineCode parentName="p">{`talonsTarget`}</inlineCode>{` from the available targets in `}<inlineCode parentName="p">{`@magento/peregrine`}</inlineCode>{` and wraps the `}<inlineCode parentName="p">{`useProductFullDetail()`}</inlineCode>{` function call with your extension.`}</p>
    <p>{`Since talon wrappers must be ES modules, this file also taps into the `}<inlineCode parentName="p">{`specialFeatures`}</inlineCode>{` target from `}<inlineCode parentName="p">{`@magento/pwa-buildpack`}</inlineCode>{` to set the `}<inlineCode parentName="p">{`esModules`}</inlineCode>{` flag to `}<inlineCode parentName="p">{`true`}</inlineCode>{`.`}</p>
    <h2 {...{
      "id": "make-a-data-fetch-hook"
    }}>{`Make a data fetch hook`}</h2>
    <p>{`Create a data fetch hook to query the backend for more data.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-sh"
      }}>{`mkdir -p src/hooks && touch src/hooks/useProductCategoriesList.js
`}</code></pre>
    <p>{`Inside the `}<inlineCode parentName="p">{`useProductCategoriesList.js`}</inlineCode>{` file, add the following content:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-js"
      }}>{`import { useMemo } from "react";
import { useQuery } from "@apollo/react-hooks";
import gql from "graphql-tag";

const GET_PRODUCT_CATEGORIES = gql\`
  query getProductCategories($urlKey: String!) {
    products(filter: { url_key: { eq: $urlKey } }) {
      items {
        categories {
          name
          url_path
        }
      }
    }
  }
\`;
const useProductCategoriesList = (props) => {
  const { urlKey } = props;

  const { error, loading, data } = useQuery(GET_PRODUCT_CATEGORIES, {
    fetchPolicy: "cache-and-network",
    nextFetchPolicy: "cache-first",
    variables: {
      urlKey: urlKey,
    },
  });

  const categories = useMemo(() => {
    if (data && data.products.items[0]) {
      return data.products.items[0].categories;
    }
    return null;
  }, [data]);

  return {
    error,
    isLoading: loading,
    categories,
  };
};

export default useProductCategoriesList;
`}</code></pre>
    <p>{`This code defines a GraphQL query that fetches a list of category names and URL for a product at a specific URL key.
It returns a hook that sends the query to the backend and returns data about the request, which includes the results.`}</p>
    <h2 {...{
      "id": "create-the-talon-wrapper-module"
    }}>{`Create the talon wrapper module`}</h2>
    <p>{`A talon wrapper module wraps around an existing talon and injects code around the implementation logic the talon executes.`}</p>
    <p>{`You can define this module anywhere in your project, but for this tutorial create it under `}<inlineCode parentName="p">{`src/targets`}</inlineCode>{`.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-sh"
      }}>{`touch src/targets/wrapper.js
`}</code></pre>
    <p>{`Inside `}<inlineCode parentName="p">{`wrapper.js`}</inlineCode>{`, add the following content:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-js"
      }}>{`import useProductCategoriesList from "../hooks/useProductCategoriesList";

const wrapUseProductFullDetails = (original) => {
  return function useProductFullDetails(props, ...restArgs) {
    console.log("Calling new useProductFullDetails() function!");

    const { product } = props;

    const categoriesListData = useProductCategoriesList({
      urlKey: product.url_key,
    });

    const { productDetails, ...defaultReturnData } = original(
      props,
      ...restArgs
    );

    return {
      ...defaultReturnData,
      productDetails: {
        ...productDetails,
        categoriesList: categoriesListData,
      },
    };
  };
};

export default wrapUseProductFullDetails;
`}</code></pre>
    <p>{`This module exports a function that returns a new `}<inlineCode parentName="p">{`useProductFullDetails()`}</inlineCode>{` function.
Whenever a component calls the original `}<inlineCode parentName="p">{`useProductFullDetails()`}</inlineCode>{` talon, it calls this new function instead.`}</p>
    <p>{`The module imports and uses the `}<inlineCode parentName="p">{`useProductCategoriesList()`}</inlineCode>{` hook defined in a previous step to get the `}<inlineCode parentName="p">{`categoriesListData`}</inlineCode>{`, which contains information about the request.
The code uses the `}<inlineCode parentName="p">{`props`}</inlineCode>{` normally passed into the `}<inlineCode parentName="p">{`original`}</inlineCode>{` function to get the value of the `}<inlineCode parentName="p">{`urlKey`}</inlineCode>{` for the GraphQL request.`}</p>
    <p>{`The extensibility framework provides the `}<inlineCode parentName="p">{`original`}</inlineCode>{` talon function when it executes the wrapper module.
The new `}<inlineCode parentName="p">{`useProductFullDetails()`}</inlineCode>{` function calls this function and uses the props data result as a starting point for the final returned value.`}</p>
    <p>{`The final return value is combination of the original props data and the categories list data returned by the hook.`}</p>
    <h2 {...{
      "id": "update-the-package-entry-point"
    }}>{`Update the package entry point`}</h2>
    <p>{`Update the `}<inlineCode parentName="p">{`package.json`}</inlineCode>{` file and set the main entry point to the talon wrapper module.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-diff"
      }}>{`  "publishConfig": {
    "access": "public"
  },
- "main": "index.js",
+ "main": "src/targets/wrapper.js",
  "license": "(OSL-3.0 OR AFL-3.0)",
`}</code></pre>
    <p>{`This indicates which module to return when another project uses this package as a dependency.
This is an important step because the intercept file in this project tells the extensibility framework to wrap the `}<inlineCode parentName="p">{`useProductFullDetails()`}</inlineCode>{` talon with the module this package provides.`}</p>
    <h2 {...{
      "id": "test-on-a-local-instance"
    }}>{`Test on a local instance`}</h2>
    <p>{`Install this extension in a local storefront project to test its functionality.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-sh"
      }}>{`yarn add --dev file:/path/to/your/extension/project
`}</code></pre>
    <p>{`This will add a `}<inlineCode parentName="p">{`devDependencies`}</inlineCode>{` entry to your storefront project's `}<inlineCode parentName="p">{`package.json`}</inlineCode>{` that looks like the following:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-diff"
      }}>{`  "memory-fs": "~0.4.1",
+ "my-extension": "file:/path/to/your/extension/project"
  "prettier": "~1.16.4",
`}</code></pre>
    <p>{`Now, when you navigate to a product page, the following message appears in the console:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-text"
      }}>{`"Calling new useProductFullDetails() function!".
`}</code></pre>
    <p>{`Use additional `}<inlineCode parentName="p">{`console.log()`}</inlineCode>{` calls to verify the application calls the data fetch hook.`}</p>
    <p>{`To test the new props data the wrapped talon returns, you will need to create a copy of the ProductFullDetails component and alter it to log or render the new data.`}</p>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      